import { FormWrapper, Label } from '@ardoq/forms';
import { IconName } from '@ardoq/icons';
import { modal, ModalSize, ModalTemplate } from '@ardoq/modal';
import { SelectOption } from '@ardoq/select';
import { useState } from 'react';
import { Locale } from '@ardoq/locale';
import { ArdoqId } from '@ardoq/api-types';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import {
  Fields,
  AdditionalFilterOptionsFunc,
  GetAISuggestions,
  FetchComponentOptionsWithQuery,
} from '../../types';
import { SurveyQuestionOptions } from '../QuestionOptions/SurveyQuestionOptions';
import { IconButton } from '@ardoq/button';

type EditReferenceModalProps = {
  surveyId: ArdoqId;
  questionId: ArdoqId;
  questionLabel: string;
  componentId: ArdoqId | undefined;
  currentLocale: Locale;
  i18nLocale: I18nLocale;
  fields: Fields;
  identifierFieldName: string | undefined;
  allowComponentCreation: boolean;
  resolve: (resultValue: SelectOption<string> | false) => void;
  additionalFilterOptions?: AdditionalFilterOptionsFunc;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
};

const EditReferenceModal = ({
  surveyId,
  questionId,
  questionLabel,
  componentId,
  currentLocale,
  i18nLocale,
  fields,
  identifierFieldName,
  allowComponentCreation,
  resolve,
  additionalFilterOptions,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
}: EditReferenceModalProps) => {
  const [answer, setAnswer] = useState<SelectOption<string> | null>(null);

  const selectedValue = answer ? { label: answer.label } : null;

  const handleCancel = () => {
    resolve(false);
  };

  const handleSubmit = () => {
    if (!answer) return resolve(false);
    resolve(answer);
  };

  return (
    <ModalTemplate
      modalSize={ModalSize.S}
      primaryButtonText={t(i18nLocale, 'surveysUi:reference.done', 'Done')}
      isPrimaryButtonDisabled={!answer}
      secondaryButtonText={t(
        i18nLocale,
        'surveysUi:reference.cancel',
        'Cancel'
      )}
      onSecondaryButtonClick={handleCancel}
      onPrimaryButtonClick={handleSubmit}
      text={
        <FormWrapper>
          <Label>
            {t(
              i18nLocale,
              'surveysUi:reference.selectComponent',
              'Select component'
            )}
          </Label>
          <SurveyQuestionOptions
            surveyId={surveyId}
            questionId={questionId}
            questionLabel={questionLabel}
            componentId={componentId}
            isMulti={false}
            isCreatable={allowComponentCreation}
            value={selectedValue}
            userFeedback={null}
            currentLocale={currentLocale}
            i18nLocale={i18nLocale}
            fields={fields}
            identifierFieldName={identifierFieldName}
            onChange={option => {
              if (!option) return;
              setAnswer(option as SelectOption<string> | null);
            }}
            additionalFilterOptions={additionalFilterOptions}
            getAISuggestions={getAISuggestions}
            fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
          />
        </FormWrapper>
      }
    />
  );
};

type OpenEditModalArgs = {
  surveyId: ArdoqId;
  questionId: ArdoqId;
  questionLabel: string;
  componentId: ArdoqId | undefined;
  currentLocale: Locale;
  i18nLocale: I18nLocale;
  fields: Fields;
  identifierFieldName: string | undefined;
  allowComponentCreation: boolean;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
  additionalFilterOptions?: AdditionalFilterOptionsFunc;
};

const openEditModal = ({
  surveyId,
  questionId,
  questionLabel,
  componentId,
  currentLocale,
  i18nLocale,
  fields,
  identifierFieldName,
  allowComponentCreation,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  additionalFilterOptions,
}: OpenEditModalArgs) => {
  return modal<SelectOption<string>>(resolve => (
    <EditReferenceModal
      surveyId={surveyId}
      questionId={questionId}
      questionLabel={questionLabel}
      componentId={componentId}
      currentLocale={currentLocale}
      i18nLocale={i18nLocale}
      fields={fields}
      identifierFieldName={identifierFieldName}
      allowComponentCreation={allowComponentCreation}
      resolve={resolve}
      additionalFilterOptions={additionalFilterOptions}
      getAISuggestions={getAISuggestions}
      fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
    />
  ));
};

type EditRefererences = {
  surveyId: ArdoqId;
  questionId: ArdoqId;
  questionLabel: string;
  componentId: ArdoqId | undefined;
  isLocked: boolean;
  readonly: boolean;
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  fields: Fields;
  identifierFieldName: string | undefined;
  allowComponentCreation: boolean;
  additionalFilterOptions?: AdditionalFilterOptionsFunc;
  editReference: (componentOption: SelectOption<string>) => void;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
};

export const EditReferenceButton = ({
  surveyId,
  questionId,
  questionLabel,
  componentId,
  isLocked,
  readonly,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  fields,
  identifierFieldName,
  allowComponentCreation,
  additionalFilterOptions,
  editReference,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
}: EditRefererences) => {
  if (isLocked || readonly) {
    return <></>;
  }
  return (
    <IconButton
      label={t(i18nLocale, 'surveysUi:reference.editLabel', 'Edit')}
      iconName={IconName.EDIT}
      onClick={e => {
        e.stopPropagation();
        openEditModal({
          surveyId,
          questionId,
          questionLabel,
          componentId,
          currentLocale,
          i18nLocale,
          fields,
          identifierFieldName,
          allowComponentCreation,
          getAISuggestions,
          fetchComponentOptionsWithQuery,
          additionalFilterOptions,
        }).then(componentOption => {
          if (componentOption) editReference(componentOption);
        });
      }}
      data-test-id="edit-reference-button"
      aria-label={questionLabel}
    />
  );
};
